import React, { useEffect, useRef, useState } from 'react';
import ConversationBubble from './ConversationBubble';
import type { VoiceMessage, VoiceSession } from '../utils/voiceSessionStorage';
import './GlobalVoiceAssistantUI.css';

function formatMessagesForClipboard(messages: VoiceMessage[]): string {
  return messages
    .map((msg) => {
      const label = msg.role === 'user' ? 'You' : 'EZAccounting.ai';
      const time = msg.timestamp ? new Date(msg.timestamp).toLocaleString() : '';
      const lines = [`${label}${time ? ` (${time})` : ''}:`, msg.content];

      if (msg.card) {
        const cardParts = [msg.card.title, msg.card.subtitle, msg.card.amount, msg.card.status].filter(Boolean);
        lines.push(`[${msg.card.type}] ${cardParts.join(' · ')}`);
      }

      return lines.join('\n');
    })
    .join('\n\n');
}

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  pastSessions: VoiceSession[];
  selectedSession?: VoiceSession | null;
  onSelectSession: (session: VoiceSession) => void;
  onLoadSession: (session: VoiceSession) => void;
  onLoadSelectedSession: () => void;
  /** Revert the chat to a specific message (optionally loading another session first). */
  onRewindToMessage?: (messageId: string, sessionId?: string) => void;
  onStartNewChat: () => void;
  onClearHistory: () => void;
  messages: VoiceMessage[];
  notify?: (message: string, type?: string) => void;
}

const HistoryModal: React.FC<HistoryModalProps> = ({
  isOpen,
  onClose,
  pastSessions,
  selectedSession,
  onSelectSession,
  onLoadSession,
  onRewindToMessage,
  onStartNewChat,
  onClearHistory,
  messages,
  notify,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState<'current' | 'sessions'>('current');
  const [copied, setCopied] = useState(false);
  const historyScrollRef = useRef<HTMLDivElement>(null);

  const filteredSessions = pastSessions.filter(
    (s) => !searchTerm || s.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const previewSession =
    view === 'current' && selectedSession?.messages?.length ? selectedSession : null;

  const displayMessages = previewSession
    ? previewSession.messages
    : view === 'current'
      ? messages
      : [];

  useEffect(() => {
    if (!isOpen || view !== 'current') return;
    const scrollEl = historyScrollRef.current;
    if (!scrollEl) return;
    scrollEl.scrollTop = scrollEl.scrollHeight;
  }, [displayMessages, isOpen, view]);

  if (!isOpen) return null;

  const handleCopyChat = async () => {
    if (displayMessages.length === 0) return;

    try {
      await navigator.clipboard.writeText(formatMessagesForClipboard(displayMessages));
      setCopied(true);
      notify?.('Chat copied to clipboard', 'success');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      notify?.('Failed to copy chat', 'error');
    }
  };

  return (
    <div className="gva-history-panel" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="gva-history-sheet">
        <div className="gva-history-handle">
          <div className="gva-history-handle-bar" />
        </div>

        <div className="gva-history-header">
          <h2 className="gva-history-title">Chat History</h2>
          <button type="button" className="gva-history-close" onClick={onClose} aria-label="Close history">
            ×
          </button>
        </div>

        <div className="gva-history-search">
          <input
            type="text"
            placeholder="Search conversations…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="gva-history-tabs">
          <button
            type="button"
            className={`gva-history-tab${view === 'current' ? ' is-active' : ''}`}
            onClick={() => setView('current')}
          >
            Current
          </button>
          <button
            type="button"
            className={`gva-history-tab${view === 'sessions' ? ' is-active' : ''}`}
            onClick={() => setView('sessions')}
          >
            All ({pastSessions.length})
          </button>
        </div>

        <div className="gva-history-content" ref={historyScrollRef}>
          {view === 'sessions' && filteredSessions.length === 0 && (
            <p className="gva-history-empty">No past conversations yet. Start talking!</p>
          )}

          {view === 'sessions' && filteredSessions.length > 0 && (
            <div className="gva-history-session-list">
              {filteredSessions.map((session) => (
                <button
                  key={session.id}
                  type="button"
                  className={`gva-history-session${selectedSession?.id === session.id ? ' is-selected' : ''}`}
                  onClick={() => {
                    onSelectSession(session);
                    setView('current');
                  }}
                >
                  <div className="gva-history-session-title">{session.title}</div>
                  <div className="gva-history-session-meta">
                    {session.messages.length} messages · {new Date(session.updatedAt).toLocaleDateString()}
                  </div>
                </button>
              ))}
            </div>
          )}

          {view === 'current' &&
            (displayMessages.length === 0 ? (
              <p className="gva-history-empty">No messages in this conversation.</p>
            ) : (
              <div className="gva-history-thread">
                {previewSession && (
                  <p className="gva-history-preview-label">Preview: {previewSession.title}</p>
                )}
                {displayMessages.map((msg, i) => (
                  <ConversationBubble
                    key={msg.id}
                    message={msg}
                    dark
                    onRewind={
                      onRewindToMessage && (previewSession || i < displayMessages.length - 1)
                        ? () => onRewindToMessage(msg.id, previewSession?.id)
                        : undefined
                    }
                  />
                ))}
              </div>
            ))}
        </div>

        <div className="gva-history-footer">
          <button type="button" className="gva-history-btn gva-history-btn-danger" onClick={onClearHistory}>
            Clear All
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {view === 'current' && displayMessages.length > 0 && (
              <button
                type="button"
                className="gva-history-btn gva-history-btn-secondary"
                onClick={handleCopyChat}
                title="Copy conversation to clipboard"
              >
                <span className="material-icons gva-history-btn-icon">content_copy</span>
                {copied ? 'Copied!' : 'Copy Chat'}
              </button>
            )}
            {previewSession && view === 'current' && (
              <button type="button" className="gva-history-btn gva-history-btn-primary" onClick={() => onLoadSession(previewSession)}>
                Load Session
              </button>
            )}
            <button type="button" className="gva-history-btn gva-history-btn-primary" onClick={onStartNewChat}>
              New Chat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryModal;
